import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  RefreshCw,
  Download
} from "lucide-react";
import TaskStatusService from "@/services/taskStatusService";
import { TaskHistory, TaskExecution, TaskStatus } from "@/types/taskStatus";

interface TaskHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskKey: string;
}

const TaskHistoryModal: React.FC<TaskHistoryModalProps> = ({
  isOpen,
  onClose,
  taskKey,
}) => {
  const [history, setHistory] = useState<TaskHistory | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(20);
  
  // Filter state
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  // Fetch task history
  const fetchHistory = useCallback(async (page: number = 1) => {
    if (!taskKey) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const options = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
      };
      
      const data = await TaskStatusService.getTaskHistory(taskKey, options);
      setHistory(data);
    } catch (err) {
      setError("Failed to load task history. Please try again.");
      console.error("Error fetching task history:", err);
    } finally {
      setLoading(false);
    }
  }, [taskKey, pageSize, startDate, endDate]);

  // Load data when modal opens or filters change
  useEffect(() => {
    if (isOpen && taskKey) {
      setCurrentPage(1);
      fetchHistory(1);
    }
  }, [isOpen, taskKey, fetchHistory]);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchHistory(newPage);
  };

  // Handle date filter change
  const handleDateFilterChange = () => {
    setCurrentPage(1);
    fetchHistory(1);
  };

  // Reset filters
  const handleResetFilters = () => {
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  // Status badge helper
  const getStatusBadge = (status: TaskStatus) => {
    switch (status) {
      case 'COMPLETED':
        return <Badge variant="default" className="bg-green-100 text-green-800 hover:bg-green-100"><CheckCircle2 className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'FAILED':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
      case 'PROCESSING':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800 hover:bg-blue-100"><Clock className="w-3 h-3 mr-1" />Processing</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Format date for input (YYYY-MM-DD)
  const formatDateForInput = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  // Calculate duration in a human-readable format
  const formatDuration = (minutes: number | null): string => {
    if (minutes === null) return "N/A";
    if (minutes < 1) return "< 1 min";
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.round(minutes % 60);
    return `${hours}h ${remainingMinutes}m`;
  };

  // Calculate pagination info
  const totalPages = history ? Math.ceil(history.total_count / pageSize) : 0;
  const startItem = history ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = history ? Math.min(currentPage * pageSize, history.total_count) : 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Task History: {taskKey}
          </DialogTitle>
          <DialogDescription>
            Detailed execution history and performance metrics for this task
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4 pb-4 border-b">
            <div className="flex flex-col sm:flex-row gap-2 flex-1">
              <div className="flex flex-col">
                <label className="text-xs font-medium text-gray-600 mb-1">Start Date</label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full sm:w-40"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs font-medium text-gray-600 mb-1">End Date</label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full sm:w-40"
                />
              </div>
            </div>
            
            <div className="flex gap-2 self-end">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleDateFilterChange}
                disabled={loading}
              >
                <Calendar className="w-4 h-4 mr-1" />
                Apply
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleResetFilters}
                disabled={loading}
              >
                Reset
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => fetchHistory(currentPage)}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto">
            {loading && !history ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin mr-2" />
                Loading task history...
              </div>
            ) : error ? (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : !history || history.executions.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No execution history found for this task</p>
                {(startDate || endDate) && (
                  <p className="text-sm mt-2">Try adjusting the date range or clearing filters</p>
                )}
              </div>
            ) : (
              <>
                {/* Summary Info */}
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-600">Total Executions:</span>
                      <span className="ml-2">{history.total_count}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Showing:</span>
                      <span className="ml-2">{startItem}-{endItem}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Page:</span>
                      <span className="ml-2">{currentPage} of {totalPages}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Task:</span>
                      <span className="ml-2 font-mono text-xs">{taskKey}</span>
                    </div>
                  </div>
                </div>

                {/* History Table */}
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Started</TableHead>
                        <TableHead>Completed</TableHead>
                        <TableHead>Duration</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {history.executions.map((execution, index) => (
                        <TableRow key={`${execution.task_date}-${index}`}>
                          <TableCell className="font-medium">
                            {execution.task_date}
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(execution.status)}
                          </TableCell>
                          <TableCell className="text-sm">
                            {formatDate(execution.created_at)}
                          </TableCell>
                          <TableCell className="text-sm">
                            {formatDate(execution.updated_at)}
                          </TableCell>
                          <TableCell className="text-sm">
                            {formatDuration(execution.execution_duration_minutes)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </div>

          {/* Pagination */}
          {history && history.executions.length > 0 && totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {startItem} to {endItem} of {history.total_count} executions
              </p>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1 || loading}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
                
                <span className="text-sm px-3 py-1 bg-gray-100 rounded">
                  {currentPage} of {totalPages}
                </span>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages || loading}
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TaskHistoryModal; 